import { useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { Button } from '@ui-component-library/components/Button';
import { Icon } from '@ui-component-library/components/Icon';
import { Text } from '@ui-component-library/components/Text';

import {
  dividerClasses,
  searchResultsClasses,
  searchResultsContainerClasses,
  searchResultsItemClasses,
  searchResultsWrapperClasses,
  wrapperClasses
} from './SearchInput.styles';
import type { SearchInputProps } from './SearchInput.types';

export const SearchInput = ({
  className,
  disabled = false,
  placeholder,
  value: valueProp,
  onChange,
  onSelect,
  results = []
}: SearchInputProps) => {
  const [internalValue, setInternalValue] = useState('');
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  const inputRef = useRef<HTMLInputElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isBlurring, setIsBlurring] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);

  const showResults = (isFocused || isBlurring) && results.length > 0;

  const handleClear = () => {
    onChange('');
    if (!isControlled) {
      setInternalValue('');
    }
    inputRef.current?.focus();
  };

  useLayoutEffect(() => {
    if (showResults && resultRef.current) {
      setContainerWidth(resultRef.current.getBoundingClientRect().width + 2);
    }
  }, [showResults, results.length]);

  const handleResultClick = (result: { value: string; label: string }) => {
    onSelect(result);
    if (!isControlled) {
      setInternalValue(result.label);
    }
    setIsBlurring(false); // Immediately hide after selection
  };

  const handleOnChange = (inputValue: string) => {
    if (inputValue === value) {
      return;
    }

    if (!isControlled) {
      setInternalValue(inputValue);
    }
    onChange(inputValue);
  };

  const onFocus = () => {
    setIsFocused(true);
    setIsBlurring(false);
  };

  const onBlur = () => {
    setIsFocused(false);
    setIsBlurring(true);

    setTimeout(() => {
      setIsBlurring(false);
    }, 200);
  };

  const showClearButton = Boolean(value) && !disabled;

  return (
    <div
      className={cn(
        wrapperClasses({
          disabled
        }),
        'relative',
        className
      )}
    >
      <input
        ref={inputRef}
        style={{
          backgroundColor: 'var(--color-white-50, #fafafa)',
          width: '100%',
          height: '100%',
          padding: '16px',
          paddingRight: showClearButton ? '44px' : '16px',
          borderRadius: '12px',
          fontSize: '14px',
          border: '1px',
          borderColor: 'rgba(22, 22, 34, 0.09)',
          letterSpacing: '-0.3px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          borderStyle: 'solid',
          transition: 'all 0.2s ease',
          outline: 'none'
        }}
        placeholder={placeholder}
        name="search"
        onChange={(eventOrValue) => {
          const nextValue =
            typeof eventOrValue === 'string' ? eventOrValue : (eventOrValue?.target?.value ?? '');
          handleOnChange(nextValue);
        }}
        type="text"
        className="border-transparent"
        onFocus={onFocus}
        onBlur={onBlur}
        value={value}
      />
      {showClearButton && (
        <Button
          variant="tertiary"
          size="icon"
          aria-label="Rensa"
          className="text-charcoal-500 absolute top-[22%] right-3 grid h-8 w-8 -translate-y-1/2 place-items-center transition-all"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
        >
          <Icon name="CrossFilled" size="large" className="block shrink-0" />
        </Button>
      )}
      <div className={searchResultsWrapperClasses()} ref={resultRef}>
        <div
          style={{
            width: containerWidth
          }}
          className={cn(searchResultsContainerClasses({ showResults }))}
        >
          <div className={dividerClasses()} />

          <div className={searchResultsClasses()}>
            {results?.map((result) => (
              <div
                key={result.value}
                className={searchResultsItemClasses({ disabled: false })}
                onClick={() => handleResultClick(result)}
              >
                <Text variant="labelLargeMedium" className="text-charcoal-500">
                  {result.label}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
